import * as React from "react";

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  type DragEndEvent,
  type DragStartEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@repo/backend";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui/components/command";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from "@repo/ui/components/field";
import { Input } from "@repo/ui/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/popover";
import { Switch } from "@repo/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/components/table";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useBlocker } from "@tanstack/react-router";
import { useAction, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import {
  BotIcon,
  CheckIcon,
  ChevronDownIcon,
  Clock3Icon,
  Gamepad2Icon,
  GripVerticalIcon,
  Loader2Icon,
  RefreshCwIcon,
  Settings2Icon,
  ShieldCheckIcon,
  WandSparklesIcon,
} from "lucide-react";
import { toast } from "sonner";

import {
  DiscordCategoryMultiSelect,
  DiscordChannelMultiSelect,
  DiscordChannelSelect,
  type DiscordChannelRow,
} from "@/components/settings/discord-channel-select";
import { featureFlags } from "@/config";

export const Route = createFileRoute("/_authed/setting/discord")({
  head: () => ({ meta: [{ title: "Discord bot" }] }),
  component: RouteComponent,
});

type RoleRow = {
  id: string;
  name: string;
  color: string;
  position: number;
  managed: boolean;
};

type RosterMemberRow = {
  id: string;
  username: string;
  displayName: string;
  avatar: string | null;
  roles: string[];
  workgrid: { userId: string; name: string; role: string; isActive: boolean } | null;
};

type DiscordConfig = {
  guildId?: string;
  commandToggles: {
    specialRequest?: boolean;
  };
  agent: {
    maxResponseChars: number;
    respondInDirectMessages: boolean;
    respondToMentions: boolean;
    respondToReplies: boolean;
    allowedChannelIds: string[];
    allowedCategoryIds: string[];
  };
  timeTracking: {
    enabled: boolean;
    allowedRoleIds: string[];
    allowedDiscordUserIds: string[];
    allowDirectMessages: boolean;
    allowGuildSlashCommands: boolean;
    requireStage: boolean;
  };
  schedule: {
    destinationChannelId?: string;
    roleIds: string[];
  };
  gameChannelRouting: Array<{
    gameId: string;
    gameName: string;
    channelId: string;
  }>;
  discordGameOrder: string[];
};

type GameRow = {
  id: string;
  name: string;
  section?: string;
  stageName?: string;
};

const createDefaultConfig = (): DiscordConfig => ({
  guildId: undefined,
  commandToggles: {
    specialRequest: false,
  },
  agent: {
    maxResponseChars: 4_000,
    respondInDirectMessages: true,
    respondToMentions: true,
    respondToReplies: true,
    allowedChannelIds: [],
    allowedCategoryIds: [],
  },
  timeTracking: {
    enabled: false,
    allowedRoleIds: [],
    allowedDiscordUserIds: [],
    allowDirectMessages: true,
    allowGuildSlashCommands: true,
    requireStage: false,
  },
  schedule: {
    destinationChannelId: undefined,
    roleIds: [],
  },
  gameChannelRouting: [],
  discordGameOrder: [],
});

const normalizeConfig = (value: (typeof api.discord.getConfig)["_returnType"]): DiscordConfig => {
  const defaults = createDefaultConfig();
  const legacySchedule = value?.schedule as { destinationChannelIds?: string[] } | undefined;

  return {
    guildId: value?.guildId ?? defaults.guildId,
    commandToggles: {
      specialRequest:
        value?.commandToggles?.specialRequest ?? defaults.commandToggles.specialRequest,
    },
    agent: {
      maxResponseChars: value?.agent?.maxResponseChars ?? defaults.agent.maxResponseChars,
      respondInDirectMessages:
        value?.agent?.respondInDirectMessages ?? defaults.agent.respondInDirectMessages,
      respondToMentions: value?.agent?.respondToMentions ?? defaults.agent.respondToMentions,
      respondToReplies: value?.agent?.respondToReplies ?? defaults.agent.respondToReplies,
      allowedChannelIds: value?.agent?.allowedChannelIds ?? defaults.agent.allowedChannelIds,
      allowedCategoryIds: value?.agent?.allowedCategoryIds ?? defaults.agent.allowedCategoryIds,
    },
    timeTracking: {
      enabled: value?.timeTracking?.enabled ?? defaults.timeTracking.enabled,
      allowedRoleIds: value?.timeTracking?.allowedRoleIds ?? defaults.timeTracking.allowedRoleIds,
      allowedDiscordUserIds:
        value?.timeTracking?.allowedDiscordUserIds ?? defaults.timeTracking.allowedDiscordUserIds,
      allowDirectMessages:
        value?.timeTracking?.allowDirectMessages ?? defaults.timeTracking.allowDirectMessages,
      allowGuildSlashCommands:
        value?.timeTracking?.allowGuildSlashCommands ??
        defaults.timeTracking.allowGuildSlashCommands,
      requireStage: value?.timeTracking?.requireStage ?? defaults.timeTracking.requireStage,
    },
    schedule: {
      destinationChannelId:
        value?.schedule?.destinationChannelId ??
        legacySchedule?.destinationChannelIds?.[0] ??
        defaults.schedule.destinationChannelId,
      roleIds: value?.schedule?.roleIds ?? defaults.schedule.roleIds,
    },
    gameChannelRouting: value?.gameChannelRouting ?? defaults.gameChannelRouting,
    discordGameOrder: value?.discordGameOrder ?? defaults.discordGameOrder,
  };
};

function RouteComponent() {
  const confirm = useConfirmationDialog();
  const queryConfig = useQuery(api.discord.getConfig, {});
  const games = useQuery(api.gameTitles.list, {});
  const upsertConfig = useMutation(api.discord.upsertConfig);
  const listChannels = useAction(api.discord.listChannels);
  const listRoles = useAction(api.discord.listRoles);
  const listRosterCandidates = useAction(api.discord.listRosterCandidates);
  const [savedConfig, setSavedConfig] = React.useState<DiscordConfig>(() => createDefaultConfig());
  const [guild, setGuild] = React.useState<{ id: string; name: string } | null>(null);
  const [channels, setChannels] = React.useState<DiscordChannelRow[]>([]);
  const [roles, setRoles] = React.useState<RoleRow[]>([]);
  const [rosterMembers, setRosterMembers] = React.useState<RosterMemberRow[]>([]);
  const [isLoadingChannels, setIsLoadingChannels] = React.useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = React.useState(false);
  const [isLoadingRosterMembers, setIsLoadingRosterMembers] = React.useState(false);
  const [activeGameId, setActiveGameId] = React.useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const form = useForm({
    defaultValues: savedConfig,
    onSubmit: async ({ value }) => {
      try {
        const result = await upsertConfig({
          config: {
            ...value,
            gameChannelRouting: value.gameChannelRouting.filter((entry) => entry.channelId),
          },
        });
        const normalized = normalizeConfig(result.config);
        setSavedConfig(normalized);
        form.reset(normalized);
        toast.success("Discord bot configuration saved");
      } catch (error) {
        if (error instanceof ConvexError && error.data === "FORBIDDEN") {
          toast.error("Only lead users can update Discord configuration.");
          return;
        }

        toast.error(
          error instanceof Error ? error.message : "Failed to save Discord configuration",
        );
      }
    },
  });

  React.useEffect(() => {
    if (!queryConfig) return;
    const normalized = normalizeConfig(queryConfig);
    setSavedConfig(normalized);
    form.reset(normalized);
  }, [form, queryConfig]);

  const loadChannels = React.useCallback(async () => {
    setIsLoadingChannels(true);
    try {
      const result = await listChannels({ guildId: queryConfig?.guildId });

      if (!result.ok) {
        toast.error(result.error ?? "Failed to load Discord channels", {
          id: "discord-channels-load-error",
        });
        setChannels([]);
        setGuild(null);
        return;
      }

      toast.dismiss("discord-channels-load-error");
      setGuild(result.guild ?? null);
      setChannels(result.channels as DiscordChannelRow[]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to load Discord channels", {
        id: "discord-channels-load-error",
      });
      setChannels([]);
      setGuild(null);
    } finally {
      setIsLoadingChannels(false);
    }
  }, [listChannels, queryConfig?.guildId]);

  React.useEffect(() => {
    if (!queryConfig) return;
    void loadChannels();
  }, [queryConfig !== undefined, queryConfig?.guildId, loadChannels]);

  const loadRoles = React.useCallback(async () => {
    setIsLoadingRoles(true);
    try {
      const result = await listRoles({ guildId: queryConfig?.guildId });
      if (!result.ok) {
        toast.error(result.error ?? "Failed to load Discord roles");
        setRoles([]);
        return;
      }
      setRoles(result.roles as RoleRow[]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to load Discord roles");
      setRoles([]);
    } finally {
      setIsLoadingRoles(false);
    }
  }, [listRoles, queryConfig?.guildId]);

  React.useEffect(() => {
    if (!queryConfig) return;
    void loadRoles();
  }, [queryConfig !== undefined, queryConfig?.guildId, loadRoles]);

  const dirtyRef = React.useRef(form.state.isDirty);

  React.useEffect(() => {
    dirtyRef.current = form.state.isDirty;
    const subscription = form.store.subscribe(() => {
      dirtyRef.current = form.state.isDirty;
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const {
    status: blockerStatus,
    proceed,
    reset,
  } = useBlocker({
    shouldBlockFn: () => dirtyRef.current,
    withResolver: true,
    enableBeforeUnload: () => dirtyRef.current,
  });

  React.useEffect(() => {
    if (blockerStatus !== "blocked") return;

    let isCancelled = false;
    void confirm({
      title: "Discard unsaved changes?",
      description:
        "You have unsaved Discord bot configuration changes. If you leave now, they will be lost.",
      confirmText: "Discard changes",
      confirmButtonVariant: "destructive",
      cancelText: "Stay on page",
    }).then((shouldLeave) => {
      if (isCancelled) return;
      if (shouldLeave) {
        proceed?.();
        return;
      }
      reset?.();
    });

    return () => {
      isCancelled = true;
    };
  }, [blockerStatus, confirm, proceed, reset]);

  const gameRows = React.useMemo<GameRow[]>(
    () =>
      (games ?? [])
        .map((game) => ({
          id: game["_id"],
          name: game.name,
          section: game.section,
          stageName: game.stageName,
        }))
        .toSorted((a, b) => a.name.localeCompare(b.name)),
    [games],
  );

  const loadRosterMembers = React.useCallback(async () => {
    setIsLoadingRosterMembers(true);
    try {
      const result = await listRosterCandidates({
        guildId: queryConfig?.guildId,
        channelId: queryConfig?.schedule.destinationChannelId,
      });
      if (!result.ok) {
        toast.error(result.error ?? "Failed to load call-times users");
        setRosterMembers([]);
        return;
      }
      setRosterMembers(
        result.members
          .map((member) => ({
            id: member.id,
            username: member.username,
            displayName: member.displayName,
            avatar: member.avatar,
            roles: member.roles,
            workgrid: null,
          }))
          .toSorted((a, b) => a.displayName.localeCompare(b.displayName)),
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to load call-times users");
      setRosterMembers([]);
    } finally {
      setIsLoadingRosterMembers(false);
    }
  }, [listRosterCandidates, queryConfig?.guildId, queryConfig?.schedule.destinationChannelId]);

  React.useEffect(() => {
    if (!queryConfig) return;
    void loadRosterMembers();
  }, [
    queryConfig !== undefined,
    queryConfig?.guildId,
    queryConfig?.schedule.destinationChannelId,
    loadRosterMembers,
  ]);

  return (
    <form
      id="discord-config-form"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Settings2Icon className="size-5" />
            Discord Bot Configuration
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure where the bot can respond and how it maps Discord channels to Media Day games.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BotIcon className="size-4" />
              Discord Server
            </CardTitle>
            <CardDescription>
              Refresh Discord metadata used by channel and category selectors.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2">
              <div className="text-sm">
                {guild ? (
                  <>
                    <span className="text-muted-foreground">Connected server:</span>
                    <span className="ml-2 font-medium">{guild.name}</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">Could not load your server yet.</span>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void loadChannels()}
                disabled={isLoadingChannels}
              >
                {isLoadingChannels ? (
                  <Loader2Icon className="size-3.5 animate-spin" />
                ) : (
                  <RefreshCwIcon className="size-3.5" />
                )}
                Refresh Channels
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <WandSparklesIcon className="size-4" />
              Bot Behavior
            </CardTitle>
            <CardDescription>
              Control AI response behavior. Slash commands /status and /debug are removed.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <form.Field
              name="agent.maxResponseChars"
              children={(field) => (
                <Field className="rounded-lg border p-3">
                  <FieldLabel>
                    <FieldContent>
                      <FieldTitle>Max response chars</FieldTitle>
                      <FieldDescription>
                        Hard cap for AI message length before truncation.
                      </FieldDescription>
                    </FieldContent>
                  </FieldLabel>
                  <Input
                    type="number"
                    min={500}
                    max={20_000}
                    value={field.state.value}
                    onChange={(event) => {
                      const maxResponseChars = Number(event.target.value);
                      field.handleChange(
                        Number.isFinite(maxResponseChars)
                          ? Math.max(500, maxResponseChars)
                          : field.state.value,
                      );
                    }}
                  />
                </Field>
              )}
            />

            <Field orientation="horizontal" className="items-center rounded-lg border p-3">
              <FieldLabel>
                <FieldContent>
                  <FieldTitle>Respond in direct messages</FieldTitle>
                  <FieldDescription>
                    Allow AI replies in direct message conversations.
                  </FieldDescription>
                </FieldContent>
              </FieldLabel>
              <form.Field
                name="agent.respondInDirectMessages"
                children={(field) => (
                  <Switch checked={field.state.value} onCheckedChange={field.handleChange} />
                )}
              />
            </Field>

            <Field orientation="horizontal" className="items-center rounded-lg border p-3">
              <FieldLabel>
                <FieldContent>
                  <FieldTitle>Respond to direct mentions</FieldTitle>
                  <FieldDescription>
                    Allow AI replies when the bot is directly mentioned.
                  </FieldDescription>
                </FieldContent>
              </FieldLabel>
              <form.Field
                name="agent.respondToMentions"
                children={(field) => (
                  <Switch checked={field.state.value} onCheckedChange={field.handleChange} />
                )}
              />
            </Field>

            <Field orientation="horizontal" className="items-center rounded-lg border p-3">
              <FieldLabel>
                <FieldContent>
                  <FieldTitle>Respond to replies</FieldTitle>
                  <FieldDescription>
                    Allow AI replies when users reply to the bot's previous message.
                  </FieldDescription>
                </FieldContent>
              </FieldLabel>
              <form.Field
                name="agent.respondToReplies"
                children={(field) => (
                  <Switch checked={field.state.value} onCheckedChange={field.handleChange} />
                )}
              />
            </Field>
          </CardContent>
        </Card>

        {featureFlags.timeTracking ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock3Icon className="size-4" />
                Discord Time Tracking
              </CardTitle>
              <CardDescription>
                Allow photographers to start, stop, check status, and correct work sessions from
                Discord DMs or private slash command responses.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <Field orientation="horizontal" className="items-center rounded-lg border p-3">
                <FieldLabel>
                  <FieldContent>
                    <FieldTitle>Enable time tracking</FieldTitle>
                    <FieldDescription>Turn on Discord-origin work sessions.</FieldDescription>
                  </FieldContent>
                </FieldLabel>
                <form.Field
                  name="timeTracking.enabled"
                  children={(field) => (
                    <Switch checked={field.state.value} onCheckedChange={field.handleChange} />
                  )}
                />
              </Field>

              <Field orientation="horizontal" className="items-center rounded-lg border p-3">
                <FieldLabel>
                  <FieldContent>
                    <FieldTitle>Allow DMs</FieldTitle>
                    <FieldDescription>Let people message the bot privately.</FieldDescription>
                  </FieldContent>
                </FieldLabel>
                <form.Field
                  name="timeTracking.allowDirectMessages"
                  children={(field) => (
                    <Switch checked={field.state.value} onCheckedChange={field.handleChange} />
                  )}
                />
              </Field>

              <Field orientation="horizontal" className="items-center rounded-lg border p-3">
                <FieldLabel>
                  <FieldContent>
                    <FieldTitle>Allow slash commands</FieldTitle>
                    <FieldDescription>
                      Guild replies are ephemeral, not public posts.
                    </FieldDescription>
                  </FieldContent>
                </FieldLabel>
                <form.Field
                  name="timeTracking.allowGuildSlashCommands"
                  children={(field) => (
                    <Switch checked={field.state.value} onCheckedChange={field.handleChange} />
                  )}
                />
              </Field>

              <Field orientation="horizontal" className="items-center rounded-lg border p-3">
                <FieldLabel>
                  <FieldContent>
                    <FieldTitle>Require stage</FieldTitle>
                    <FieldDescription>
                      Block starts that do not include a stage name.
                    </FieldDescription>
                  </FieldContent>
                </FieldLabel>
                <form.Field
                  name="timeTracking.requireStage"
                  children={(field) => (
                    <Switch checked={field.state.value} onCheckedChange={field.handleChange} />
                  )}
                />
              </Field>

              <form.Field
                name="timeTracking.allowedRoleIds"
                children={(field) => (
                  <Field>
                    <FieldLabel>
                      <FieldContent>
                        <FieldTitle>Allowed roles</FieldTitle>
                        <FieldDescription>
                          Users with any selected role can track time from Discord.
                        </FieldDescription>
                      </FieldContent>
                    </FieldLabel>
                    <DiscordRoleMultiSelect
                      roles={roles}
                      selectedRoleIds={field.state.value}
                      onSelectionChange={field.handleChange}
                      disabled={isLoadingRoles}
                    />
                  </Field>
                )}
              />

              <form.Field
                name="timeTracking.allowedDiscordUserIds"
                children={(field) => (
                  <Field>
                    <FieldLabel>
                      <FieldContent>
                        <FieldTitle>Whitelisted users</FieldTitle>
                        <FieldDescription>
                          Optional testers and exceptions from the call-times roster.
                        </FieldDescription>
                      </FieldContent>
                    </FieldLabel>
                    <DiscordUserMultiSelect
                      members={rosterMembers}
                      selectedUserIds={field.state.value}
                      onSelectionChange={field.handleChange}
                      disabled={isLoadingRosterMembers}
                    />
                    {rosterMembers.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No call-times roster cache yet. Refresh the roster from Schedule settings
                        after selecting the call-times destination channel and roles.
                      </p>
                    ) : null}
                  </Field>
                )}
              />
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheckIcon className="size-4" />
              Response Scope
            </CardTitle>
            <CardDescription>
              Choose channels and categories where the bot can answer in guild conversations. DMs
              are controlled separately above.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <form.Field
              name="agent.allowedChannelIds"
              children={(field) => (
                <Field>
                  <FieldLabel>
                    <FieldContent>
                      <FieldTitle>Allowed channels</FieldTitle>
                      <FieldDescription>
                        Exact channels where mentions/replies may trigger the bot.
                      </FieldDescription>
                    </FieldContent>
                  </FieldLabel>
                  <DiscordChannelMultiSelect
                    channels={channels}
                    selectedChannelIds={field.state.value}
                    onSelectionChange={field.handleChange}
                    disabled={isLoadingChannels}
                  />
                </Field>
              )}
            />

            <form.Field
              name="agent.allowedCategoryIds"
              children={(field) => (
                <Field>
                  <FieldLabel>
                    <FieldContent>
                      <FieldTitle>Allowed categories</FieldTitle>
                      <FieldDescription>
                        All text channels inside selected categories are allowed.
                      </FieldDescription>
                    </FieldContent>
                  </FieldLabel>
                  <DiscordCategoryMultiSelect
                    channels={channels}
                    selectedCategoryIds={field.state.value}
                    onSelectionChange={field.handleChange}
                    disabled={isLoadingChannels}
                  />
                </Field>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gamepad2Icon className="size-4" />
              Game Context Channels
            </CardTitle>
            <CardDescription>
              Map each Media Day game to a Discord channel so the bot knows which game context to
              use when answering there.
            </CardDescription>
          </CardHeader>
          <form.Field
            name="discordGameOrder"
            children={(orderField) => {
              const orderedGames = orderGameRows(gameRows, orderField.state.value);
              const activeGame = orderedGames.find((game) => game.id === activeGameId) ?? null;
              const gameIds = orderedGames.map((game) => game.id);

              const handleDragStart = (event: DragStartEvent) => {
                setActiveGameId(String(event.active.id));
              };

              const handleDragEnd = (event: DragEndEvent) => {
                const activeId = String(event.active.id);
                const overId = event.over?.id ? String(event.over.id) : null;
                setActiveGameId(null);

                if (!overId || activeId === overId) return;

                const oldIndex = gameIds.indexOf(activeId);
                const newIndex = gameIds.indexOf(overId);
                if (oldIndex === -1 || newIndex === -1) return;

                orderField.handleChange(arrayMove(gameIds, oldIndex, newIndex));
              };

              return (
                <CardContent>
                  {orderedGames.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No games found yet.</p>
                  ) : (
                    <DndContext
                      collisionDetection={closestCenter}
                      modifiers={[restrictToVerticalAxis]}
                      onDragCancel={() => setActiveGameId(null)}
                      onDragEnd={handleDragEnd}
                      onDragStart={handleDragStart}
                      sensors={sensors}
                    >
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10" />
                              <TableHead>Game</TableHead>
                              <TableHead className="w-[420px]">Context channel</TableHead>
                            </TableRow>
                          </TableHeader>
                          <SortableContext items={gameIds} strategy={verticalListSortingStrategy}>
                            <TableBody>
                              {orderedGames.map((game) => (
                                <form.Field
                                  key={game.id}
                                  name="gameChannelRouting"
                                  children={(field) => {
                                    const current = field.state.value.find(
                                      (entry) => entry.gameId === game.id,
                                    );
                                    const updateGameChannel = (channelId?: string) => {
                                      const withoutGame = field.state.value.filter(
                                        (entry) => entry.gameId !== game.id,
                                      );
                                      field.handleChange(
                                        channelId
                                          ? [
                                              ...withoutGame,
                                              { gameId: game.id, gameName: game.name, channelId },
                                            ]
                                          : withoutGame,
                                      );
                                    };

                                    return (
                                      <SortableGameRow
                                        channels={channels}
                                        disabled={isLoadingChannels}
                                        game={game}
                                        isActive={activeGameId === game.id}
                                        onChannelChange={updateGameChannel}
                                        selectedChannelId={current?.channelId}
                                      />
                                    );
                                  }}
                                />
                              ))}
                            </TableBody>
                          </SortableContext>
                        </Table>
                      </div>
                      <DragOverlay dropAnimation={null}>
                        {activeGame ? <GameRowPreview game={activeGame} /> : null}
                      </DragOverlay>
                    </DndContext>
                  )}
                </CardContent>
              );
            }}
          />
          <form.Subscribe
            selector={(state) => ({ isDirty: state.isDirty, isSubmitting: state.isSubmitting })}
            children={({ isDirty, isSubmitting }) => (
              <CardFooter className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => form.reset()}
                  disabled={!isDirty || isSubmitting}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  form="discord-config-form"
                  disabled={!isDirty || isSubmitting}
                >
                  {isSubmitting ? <Loader2Icon className="size-4 animate-spin" /> : null}
                  Save Configuration
                </Button>
              </CardFooter>
            )}
          />
        </Card>
      </div>
    </form>
  );
}

function DiscordUserMultiSelect({
  members,
  selectedUserIds,
  onSelectionChange,
  disabled = false,
}: {
  members: RosterMemberRow[];
  selectedUserIds: string[];
  onSelectionChange: (userIds: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const selectedSet = new Set(selectedUserIds);
  const selectedMembers = members.filter((member) => selectedSet.has(member.id));
  const missingUserIds = selectedUserIds.filter(
    (userId) => !members.some((member) => member.id === userId),
  );
  const query = search.trim().toLowerCase();
  const filteredMembers = query
    ? members.filter((member) =>
        `${member.displayName} ${member.username} ${member.workgrid?.name ?? ""}`
          .toLowerCase()
          .includes(query),
      )
    : members;

  const toggle = (userId: string) => {
    onSelectionChange(
      selectedSet.has(userId)
        ? selectedUserIds.filter((selectedId) => selectedId !== userId)
        : [...selectedUserIds, userId],
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      }}
    >
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className="h-auto w-full justify-between px-3 py-2"
            disabled={disabled}
          />
        }
      >
        <div className="flex min-h-6 flex-wrap items-center gap-1.5">
          {selectedMembers.length === 0 && missingUserIds.length === 0 ? (
            <span className="text-sm text-muted-foreground">Select whitelisted users...</span>
          ) : selectedMembers.length + missingUserIds.length > 3 ? (
            <Badge variant="secondary">
              {selectedMembers.length + missingUserIds.length} users selected
            </Badge>
          ) : (
            <>
              {selectedMembers.map((member) => (
                <Badge key={member.id} variant="secondary">
                  {member.displayName}
                </Badge>
              ))}
              {missingUserIds.map((userId) => (
                <Badge key={userId} variant="outline">
                  {userId}
                </Badge>
              ))}
            </>
          )}
        </div>
        <ChevronDownIcon className="size-4 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent className="w-105 p-0">
        <Command shouldFilter={false} className="p-1">
          <CommandInput
            placeholder="Search call-times users..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-72">
            {filteredMembers.length === 0 ? (
              <CommandEmpty>No users found.</CommandEmpty>
            ) : (
              <CommandGroup heading="Call-times roster">
                {filteredMembers.map((member) => {
                  const selected = selectedSet.has(member.id);
                  return (
                    <CommandItem
                      key={member.id}
                      value={member.displayName}
                      onSelect={() => toggle(member.id)}
                      className="cursor-pointer gap-2"
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-medium">{member.displayName}</span>
                        <span className="truncate text-xs text-muted-foreground">
                          @{member.username}
                          {member.workgrid ? ` · ${member.workgrid.name}` : ""}
                        </span>
                      </div>
                      {member.workgrid?.isActive ? <Badge variant="outline">WorkGrid</Badge> : null}
                      {selected ? <CheckIcon className="size-4" /> : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DiscordRoleMultiSelect({
  roles,
  selectedRoleIds,
  onSelectionChange,
  disabled = false,
}: {
  roles: RoleRow[];
  selectedRoleIds: string[];
  onSelectionChange: (roleIds: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const selectedRoles = roles.filter((role) => selectedRoleIds.includes(role.id));
  const selectedSet = new Set(selectedRoleIds);
  const query = search.trim().toLowerCase();
  const filteredRoles = query
    ? roles.filter((role) => role.name.toLowerCase().includes(query))
    : roles;

  const toggle = (roleId: string) => {
    onSelectionChange(
      selectedSet.has(roleId)
        ? selectedRoleIds.filter((selectedId) => selectedId !== roleId)
        : [...selectedRoleIds, roleId],
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      }}
    >
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className="h-auto w-full justify-between px-3 py-2"
            disabled={disabled}
          />
        }
      >
        <div className="flex min-h-6 flex-wrap items-center gap-1.5">
          {selectedRoles.length === 0 ? (
            <span className="text-sm text-muted-foreground">Select allowed roles...</span>
          ) : selectedRoles.length > 3 ? (
            <Badge variant="secondary">{selectedRoles.length} roles selected</Badge>
          ) : (
            selectedRoles.map((role) => (
              <Badge key={role.id} variant="secondary">
                {role.name}
              </Badge>
            ))
          )}
        </div>
        <ChevronDownIcon className="size-4 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent className="w-105 p-0">
        <Command shouldFilter={false} className="p-1">
          <CommandInput placeholder="Search roles..." value={search} onValueChange={setSearch} />
          <CommandList className="max-h-72">
            {filteredRoles.length === 0 ? (
              <CommandEmpty>No roles found.</CommandEmpty>
            ) : (
              <CommandGroup heading="Discord roles">
                {filteredRoles.map((role) => {
                  const selected = selectedSet.has(role.id);
                  return (
                    <CommandItem
                      key={role.id}
                      value={role.name}
                      onSelect={() => toggle(role.id)}
                      className="cursor-pointer gap-2"
                    >
                      <span
                        className="size-2.5 rounded-full border"
                        style={{
                          backgroundColor: role.color === "#000000" ? "transparent" : role.color,
                        }}
                      />
                      <span className="flex-1">{role.name}</span>
                      {role.managed ? <Badge variant="outline">Managed</Badge> : null}
                      {selected ? <CheckIcon className="size-4" /> : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function orderGameRows(games: GameRow[], order: string[]) {
  const byId = new Map(games.map((game) => [game.id, game]));
  const ordered = order.flatMap((id) => {
    const game = byId.get(id);
    return game ? [game] : [];
  });
  const knownIds = new Set(order);
  return [...ordered, ...games.filter((game) => !knownIds.has(game.id))];
}

function SortableGameRow({
  game,
  channels,
  selectedChannelId,
  onChannelChange,
  disabled,
  isActive,
}: {
  game: GameRow;
  channels: DiscordChannelRow[];
  selectedChannelId?: string;
  onChannelChange: (channelId?: string) => void;
  disabled: boolean;
  isActive: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: game.id,
  });

  return (
    <TableRow
      ref={setNodeRef}
      className={isActive ? "bg-primary/5 outline-2 outline-primary/40 outline-dashed" : undefined}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : 1,
      }}
    >
      <TableCell className="w-10 align-middle">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 cursor-grab text-muted-foreground active:cursor-grabbing"
          aria-label={`Reorder ${game.name}`}
          {...attributes}
          {...listeners}
        >
          <GripVerticalIcon className="size-4" />
        </Button>
      </TableCell>
      <TableCell>
        <GameRowTitle game={game} />
      </TableCell>
      <TableCell>
        <DiscordChannelSelect
          channels={channels}
          selectedChannelId={selectedChannelId}
          onSelectionChange={onChannelChange}
          disabled={disabled}
          placeholder="Select game context channel..."
        />
      </TableCell>
    </TableRow>
  );
}

function GameRowTitle({ game }: { game: GameRow }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium">{game.name}</span>
      {game.section || game.stageName ? (
        <div className="flex gap-1.5">
          {game.section ? <Badge variant="outline">{game.section}</Badge> : null}
          {game.stageName ? <Badge variant="secondary">{game.stageName}</Badge> : null}
        </div>
      ) : null}
    </div>
  );
}

function GameRowPreview({ game }: { game: GameRow }) {
  return (
    <div className="flex w-[720px] items-center gap-3 rounded-md border bg-background p-3 shadow-lg">
      <GripVerticalIcon className="size-4 text-muted-foreground" />
      <GameRowTitle game={game} />
    </div>
  );
}
